import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { ProductOrder } from 'app/models/ProductOrder';
import { ProductionPiece } from 'app/models/ProductionPiece';
import _ from 'lodash';
import Img from 'app/components/Img';
import LoadingSpinner from 'app/containers/OrderDetails/LoadingSpinner';
import CreatePartOrderModal from 'app/containers/OrderDetails/PiecesTab/CreatePartOrderModal';
import { RiAlarmWarningFill } from 'react-icons/all';
import { FiDownload } from 'react-icons/fi';
import { pieceRef } from 'app/pages/ToursPage/tourHelpers';
import { selectCurrentPartOrder } from 'store/slices/activeContentSlice';
import { BASE_URL } from 'app/constants';

interface Props {
  loading: boolean;
}

const PiecesTab = ({ loading }: Props) => {
  const partOrder = useSelector(selectCurrentPartOrder());
  const [isCreatePartOrderModalOpen, setCreatePartOrderModalOpen] =
    useState<boolean>(false);
  const [downloadingProductId, setDownloadingProductId] = useState<
    number | undefined
  >(undefined);

  const resolvePro100ExcelUrl = (product: ProductOrder) => {
    if (!product.pro100ExcelUrl) return undefined;

    try {
      return new URL(product.pro100ExcelUrl, BASE_URL).href;
    } catch (error) {
      console.warn(
        `Failed to resolve Pro100 Excel URL for product ${product.id}`,
        error,
      );
      return product.pro100ExcelUrl;
    }
  };

  const filenameFromDisposition = (disposition: string | null) => {
    const encodedMatch = disposition?.match(/filename\*=[^']*''([^;]+)/i);
    if (encodedMatch?.[1]) {
      return decodeURIComponent(encodedMatch[1].replace(/"/g, ''));
    }

    const match = disposition?.match(/filename="?([^";]+)"?/i);
    return match?.[1];
  };

  const downloadPro100Excel = async (product: ProductOrder) => {
    const excelUrl = resolvePro100ExcelUrl(product);
    if (!excelUrl) return;

    setDownloadingProductId(product.id);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `JWT ${token}`;

      const response = await fetch(excelUrl, {
        headers,
        credentials: 'same-origin',
      });
      if (!response.ok) {
        console.warn(`Failed to download Pro100 Excel: ${response.statusText}`);
        return;
      }

      const blob = await response.blob();
      if (!blob.size) {
        console.warn('Received empty Pro100 Excel file.');
        return;
      }

      const urlObject = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename =
        filenameFromDisposition(response.headers.get('Content-Disposition')) ??
        `Pro100-${product.id}.xls`;
      link.href = urlObject;
      link.download = filename;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(urlObject);
    } catch (error) {
      console.error(
        `Error downloading Pro100 Excel for product ${product.id}:`,
        error,
      );
    } finally {
      setDownloadingProductId(undefined);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (!partOrder || partOrder.products === undefined) return null;

  return (
    <>
      <TableContainer
        className="overflow-y-auto"
        style={{ height: 'calc(100% - 170px)' }}
      >
        <Table
          stickyHeader
          size="small"
          aria-label="Pieces production informations"
        >
          <TableHead>
            <TableRow>
              <TableCell align="left" className="whitespace-no-wrap">
                Nb
              </TableCell>
              <TableCell align="left" className="whitespace-no-wrap">
                Référence
              </TableCell>
              <TableCell align="left">
                <div className="flex justify-between">
                  <div>Description</div>
                  <button
                    onClick={() => {
                      setCreatePartOrderModalOpen(true);
                    }}
                    className="py-1 px-2 border border-gray-600"
                  >
                    Créer une sous-commande
                  </button>
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partOrder.products.map((product: ProductOrder) => (
              <React.Fragment key={`product-${product.id}`}>
                <TableRow key={`T-${product.id}`} className="bg-gray-200">
                  <TableCell align="left" colSpan={3}>
                    <div className="flex items-center justify-between font-bold text-lg">
                      <div>
                        {!product.uniqueProduct &&
                        product.name &&
                        product.name !== ''
                          ? product.name
                          : '-'}
                      </div>
                      <div className="flex items-center gap-4 mr-20">
                        {product.pro100ExcelUrl && (
                          <button
                            type="button"
                            onClick={() => downloadPro100Excel(product)}
                            disabled={downloadingProductId === product.id}
                            className="inline-flex items-center gap-2 px-2 py-1 text-sm border border-gray-600 bg-white hover:bg-gray-100 disabled:opacity-60"
                            data-tip="Download Pro100 file"
                            data-for="infos"
                          >
                            <FiDownload className="h-4 w-4" />
                            {downloadingProductId === product.id
                              ? 'Downloading...'
                              : 'Pro100'}
                          </button>
                        )}
                        {product.image && (
                          <div className="w-12 h-12">
                            <Img src={product.image} alt={`preview `} />
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
                {_.orderBy(product.production, ['sorting', 'reference']).map(
                  (e: ProductionPiece) => (
                    <TableRow key={`P-${e.id}`}>
                      <TableCell align="left">{e.quantity}</TableCell>
                      <TableCell align="left">
                        <div className="flex">
                          {e.isOutOfStock && (
                            <RiAlarmWarningFill className="h-5 w-5 mx-1 bg-red text-white" />
                          )}
                          <div className="ml-1">
                            {product.uniqueProduct
                              ? 'SPECIAL'
                              : pieceRef(e.reference, e.type)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell align="left">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: product.uniqueProduct
                              ? product.name ?? ''
                              : e.descriptionHTML ?? '',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CreatePartOrderModal
        partOrder={partOrder}
        isCreatePartOrderModalOpen={isCreatePartOrderModalOpen}
        setCreatePartOrderModalOpen={setCreatePartOrderModalOpen}
      />
    </>
  );
};

export default PiecesTab;
